import { SparklesIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import FormInput from 'components/common/form-input';
import InfoTooltip from 'components/layout/info-tooltip';
import { useGenerateWindowsMutation } from 'features/api/project-api';
import { useState } from 'react';
import { refetchStructureSpaces } from 'utils/data';
import {
  applyWindowBugScreenChange,
  applyWindowPanesChange,
  getWindowBugScreenValue,
  getWindowPanesValue,
  WINDOW_BUG_SCREEN_OPTIONS,
  WINDOW_PANES_OPTIONS
} from 'utils/fenestration-defaults';
import { notification } from 'utils/ui';

export default function WindowGenerator() {
  const dispatch = useAppDispatch();
  const { estimatingStructure } = useAppSelector((state) => state.ui);
  const structureUniqueId = useAppSelector(
    (state) => state.structure.structureUniqueId
  );
  const verticalGlassDefaults = useAppSelector(
    (state) => state.data.defaultSizingItems.verticalGlass
  );

  const [width, setWidth] = useState(3);
  const [height, setHeight] = useState(4);

  const [generateWindows, { isLoading }] = useGenerateWindowsMutation();

  const handleGenerate = async () => {
    if (!structureUniqueId) {
      notification('No structure selected', 'error');
      return;
    }

    try {
      const result = await generateWindows({
        structureUniqueId,
        width,
        height
      }).unwrap();

      if (result.affectedSpaceUniqueIds.length > 0) {
        await refetchStructureSpaces(
          result.affectedSpaceUniqueIds,
          structureUniqueId
        );
      }

      notification(
        `Windows: ${result.created} added, ${result.updated} updated, ${result.deleted} removed`,
        'success'
      );
    } catch {
      notification('Failed to generate windows', 'error');
    }
  };

  const disabled = isLoading || estimatingStructure || !structureUniqueId;
  const panesValue = getWindowPanesValue(verticalGlassDefaults);
  const bugScreenValue = getWindowBugScreenValue(verticalGlassDefaults);

  return (
    <div id='window-generator' className='flex flex-col gap-3'>
      <div className='flex items-center gap-1'>
        <SparklesIcon className='h-6 w-6' />
        <span className='text-lg font-semibold'>Bulk Apply Windows</span>
      </div>
      <div className='flex flex-col gap-2'>
        <label className='flex items-center justify-between gap-2 text-sm'>
          <span className='flex items-center gap-1'>
            Window panes:
            <InfoTooltip>
              Windows with multiple panes reduce heat loss/gain through windows.
            </InfoTooltip>
          </span>
          <FormInput
            type='select'
            value={panesValue}
            options={WINDOW_PANES_OPTIONS}
            onChange={(value) => applyWindowPanesChange(dispatch, value)}
            preventDeselection
            disabled={disabled}
          />
        </label>
        <label className='flex items-center justify-between gap-2 text-sm'>
          <span className='flex items-center gap-1'>
            Window bug screens:
            <InfoTooltip>
              Windows with a bug screen provides a reduction in heat loss/gain
              through windows.
            </InfoTooltip>
          </span>
          <FormInput
            type='select'
            value={bugScreenValue}
            options={WINDOW_BUG_SCREEN_OPTIONS}
            onChange={(value) => applyWindowBugScreenChange(dispatch, value)}
            preventDeselection
            disabled={disabled}
          />
        </label>
      </div>
      <div className='flex flex-col gap-1'>
        <label className='flex items-center justify-between text-sm'>
          <span>Width (ft):</span>
          <input
            type='text'
            inputMode='decimal'
            value={width}
            onChange={(e) => setWidth(Number(e.target.value) || 0)}
            disabled={disabled}
            className='w-14 rounded border border-gray-300 px-1.5 py-0.5 text-center text-sm disabled:bg-gray-100'
          />
        </label>
        <label className='flex items-center justify-between text-sm'>
          <span>Height (ft):</span>
          <input
            type='text'
            inputMode='decimal'
            value={height}
            onChange={(e) => setHeight(Number(e.target.value) || 0)}
            disabled={disabled}
            className='w-14 rounded border border-gray-300 px-1.5 py-0.5 text-center text-sm disabled:bg-gray-100'
          />
        </label>
      </div>
      <button
        type='button'
        onClick={handleGenerate}
        disabled={disabled}
        className='w-full rounded-lg bg-blue-500 py-2 font-semibold text-white transition-colors hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50'
      >
        {isLoading ? 'Generating...' : 'Auto-Generate Windows'}
      </button>
    </div>
  );
}
